import {inject, Injectable, signal, WritableSignal} from '@angular/core';
import {AnalyticsAction} from '../../../core/analytics/enums/analytics-action';
import {VehicleSearchOrigin} from '../../../core/analytics/enums/vehicle-search-origin';
import {AnalyticsService} from '../../../core/analytics/services/analytics.service';
import {KeyDataLoader} from '../../../core/keydata/services/key-data.loader';
import {ServiceCaseManager} from '../../../core/service-case/models/service-case-manager';
import {AdvancedSearchState} from './advanced-search-state';

export const LOCAL_STORAGE_KEY_FOR_SEARCH_OVERLAY_STATE = 'searchOverlayState';
export const KEY_POOL_TAB_TITLE_RESOURCE = 'case.keyPool';

/**
 * Holds the state of the vehicle search and performs searches that identify a vehicle or service case directly from
 * the entered search string. It is shared between the service case bar, which provides the search input field, and the
 * advanced search, which provides the search tabs.
 */
@Injectable({providedIn: 'root'})
export class VehicleSearchService {
  /**
   * Matches an empty input, a 4-digit type code, a 7- or 17-digit VIN or a 36-digit service case id.
   */
  static readonly SEARCH_INPUT_VALIDATION_REGEXP: string =
    '^\\s*(|[a-zA-Z0-9]{4}|[a-zA-Z0-9]{7}|[a-zA-Z0-9]{17}|[a-zA-Z0-9\\-]{36})\\s*$';

  private static readonly MAX_RECENT_SEARCH_STRINGS: number = 10;

  private readonly serviceCaseManager: ServiceCaseManager = inject(ServiceCaseManager);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly keyDataLoader: KeyDataLoader = inject(KeyDataLoader);

  /**
   * True while the advanced search is shown instead of the currently opened app.
   */
  readonly advancedSearchVisible: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * Title resource of the tab that has to be activated the next time the advanced search is opened. If undefined, the
   * previously selected tab stays active.
   */
  readonly requestedTabTitleResource: WritableSignal<string | undefined> = signal<string | undefined>(undefined);

  readonly state: AdvancedSearchState = VehicleSearchService.readStateFromLocalStorage();

  /**
   * The recently used search strings in upper case, most recent first. The signal and the persisted state always
   * reference the same array.
   */
  readonly recentSearchStrings: WritableSignal<string[]> = signal<string[]>(this.state.recentSearchStrings);

  private static readStateFromLocalStorage(): AdvancedSearchState {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_FOR_SEARCH_OVERLAY_STATE)) ?? {
      recentSearchStrings: [],
      selectedTab: ''
    };
  }

  toggleAdvancedSearch(): void {
    this.advancedSearchVisible.set(!this.advancedSearchVisible());
  }

  openKeyPoolSearch(): void {
    this.analyticsService.postKeyPoolButtonClickedEvent();
    this.requestedTabTitleResource.set(KEY_POOL_TAB_TITLE_RESOURCE);
    this.advancedSearchVisible.set(true);
  }

  closeAdvancedSearch(): void {
    this.advancedSearchVisible.set(false);
  }

  /**
   * Checks the search input, which may contain nothing, a 4-digit type code, a 7- or 17-digit VIN or a larger service
   * case id. If a service case id is entered, the service case is loaded, otherwise a new service case is created, a
   * vehicle identified by VIN or type code and added. If the search input is empty, a new empty service case is
   * created. The service case is activated in each case.
   * @param searchInput Content of the search input field.
   * @returns True if the input was valid and a service case has been opened, false if the input was rejected.
   */
  searchAndOpenServiceCase(searchInput: string): boolean {
    if (!new RegExp(VehicleSearchService.SEARCH_INPUT_VALIDATION_REGEXP).test(searchInput ?? '')) {
      return false;
    }
    const input: string = (searchInput ?? '').trim();
    if (input.length > 17) {
      // input is a case ID
      this.serviceCaseManager.openExistingServiceCaseById(input, VehicleSearchOrigin.CASE_ID)
        .finally((): void => {
          // do nothing
        });
      this.postVehicleSearchAnalyticsEvents(VehicleSearchOrigin.CASE_ID, input);
    } else if (input.length > 4) {
      // input is a VIN
      const origin: string = input.length === 7 ? VehicleSearchOrigin.VIN7 : VehicleSearchOrigin.VIN17;
      this.serviceCaseManager.selectAndOpenServiceCaseByVin(input, origin);
      this.postVehicleSearchAnalyticsEvents(origin, input);
    } else if (input.length === 4) {
      // input is a type code
      this.serviceCaseManager.openNewServiceCaseByTypeCode(input, VehicleSearchOrigin.TYPE_CODE);
      this.postVehicleSearchAnalyticsEvents(VehicleSearchOrigin.TYPE_CODE, input);
    } else {
      // no input
      this.serviceCaseManager.openNewServiceCaseWithoutVehicle();
    }
    this.addSearchStringToRecentSearches(input);
    this.closeAdvancedSearch();
    return true;
  }

  postVehicleSearchAnalyticsEvents(origin: string, searchInput: string): void {
    this.analyticsService.postVehicleSearchEvent(AnalyticsAction.VEHICLE_SEARCH, origin);

    if (origin === VehicleSearchOrigin.VIN17 && this.keyDataLoader.getKeyDataForVin(searchInput?.trim())) {
      this.analyticsService.postVehicleSearchEvent(AnalyticsAction.VEHICLE_SEARCH, 'vin-in-key-pool');
    }
  }

  setSelectedTab(tabTitleResource: string): void {
    this.state.selectedTab = tabTitleResource;
    this.persistState();
  }

  private addSearchStringToRecentSearches(searchString: string): void {
    if (!searchString || searchString.length > 17) {
      // empty strings or service case IDs should not be included in recent searches
      return;
    }
    // add the string in upper case to the first position, keep the strings unique and limit the number of entries
    const recentSearchStrings: string[] = [searchString.toUpperCase(), ...this.recentSearchStrings()];
    this.state.recentSearchStrings =
      [...new Set(recentSearchStrings)].slice(0, VehicleSearchService.MAX_RECENT_SEARCH_STRINGS);
    this.recentSearchStrings.set(this.state.recentSearchStrings);
    this.persistState();
  }

  private persistState(): void {
    localStorage.setItem(LOCAL_STORAGE_KEY_FOR_SEARCH_OVERLAY_STATE, JSON.stringify(this.state));
  }
}
